import type { AxiosRequestConfig } from "axios";

const REDACT_HEADER_KEYS = [
  "authorization",
  "x-officer-token",
  "cookie",
  "set-cookie",
  "api-bearer",
  "x-api-key",
];

const REDACT_BODY_KEY_RX = /password|token|secret|access_token|refresh_token|api_key|bearer/i;

const REQUEST_ID_HEADER_KEYS = [
  "x-request-id",
  "x-correlation-id",
  "x-amzn-trace-id",
  "x-trace-id",
];

export function redactHeaders(
  headers: Record<string, unknown> | undefined | null
): Record<string, unknown> {
  if (!headers || typeof headers !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(headers)) {
    if (REDACT_HEADER_KEYS.includes(key.toLowerCase())) {
      out[key] = "[redacted]";
    } else {
      out[key] = (headers as Record<string, unknown>)[key];
    }
  }
  return out;
}

export function redactBody(body: unknown): unknown {
  if (body === null || body === undefined) return body;
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const obj: Record<string, unknown> = {};
    (body as FormData).forEach((value, key) => {
      const serialized =
        typeof File !== "undefined" && value instanceof File
          ? { __file: true, name: value.name, size: value.size, type: value.type }
          : value;
      if (key in obj) {
        const existing = obj[key];
        obj[key] = Array.isArray(existing) ? [...existing, serialized] : [existing, serialized];
      } else {
        obj[key] = serialized;
      }
    });
    body = obj;
  }
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      return redactBody(parsed);
    } catch {
      return body;
    }
  }
  if (Array.isArray(body)) {
    return body.map((item) => redactBody(item));
  }
  if (typeof body === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(body as Record<string, unknown>)) {
      const value = (body as Record<string, unknown>)[key];
      if (REDACT_BODY_KEY_RX.test(key)) {
        out[key] = "[redacted]";
      } else {
        out[key] = redactBody(value);
      }
    }
    return out;
  }
  return body;
}

function shellEscape(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

export function buildCurl(config: AxiosRequestConfig | undefined): string {
  if (!config) return "";
  const method = (config.method || "get").toUpperCase();
  const baseURL = config.baseURL || "";
  const rawUrl = config.url || "";
  const fullUrl = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : `${baseURL.replace(/\/$/, "")}/${rawUrl.replace(/^\//, "")}`;

  const params = config.params as Record<string, unknown> | undefined;
  let urlWithQuery = fullUrl;
  if (params && typeof params === "object") {
    const qs = Object.keys(params)
      .filter((k) => params[k] !== undefined && params[k] !== null)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`)
      .join("&");
    if (qs) {
      urlWithQuery += (fullUrl.includes("?") ? "&" : "?") + qs;
    }
  }

  const parts: string[] = [`curl -X ${method} ${shellEscape(urlWithQuery)}`];

  const redactedHeaders = redactHeaders(
    config.headers as Record<string, unknown> | undefined
  );
  for (const key of Object.keys(redactedHeaders)) {
    const value = redactedHeaders[key];
    if (value === undefined || value === null) continue;
    parts.push(`-H ${shellEscape(`${key}: ${String(value)}`)}`);
  }

  if (config.data !== undefined && config.data !== null && method !== "GET") {
    const redactedBody = redactBody(config.data);
    const bodyStr =
      typeof redactedBody === "string"
        ? redactedBody
        : JSON.stringify(redactedBody);
    parts.push(`--data-raw ${shellEscape(bodyStr)}`);
  }

  return parts.join(" \\\n  ");
}

export function extractRequestId(
  responseHeaders: Record<string, unknown> | undefined | null
): string | null {
  if (!responseHeaders || typeof responseHeaders !== "object") return null;
  const lowered: Record<string, unknown> = {};
  for (const key of Object.keys(responseHeaders)) {
    lowered[key.toLowerCase()] = (responseHeaders as Record<string, unknown>)[key];
  }
  for (const key of REQUEST_ID_HEADER_KEYS) {
    const value = lowered[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return null;
}
